import { useState, useEffect, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import dayjs from 'dayjs'
import { MainLayout } from 'views/layouts'
import {
  Container,
  LittleCard,
  CountTestAvailables,
  TextLitleCard,
  BigCard,
  MyProcessContainer,
  BigContainer,
  LitleCardsContainer,
  TopContainer,
  ChartContainer,
  MyProcessBottomContainer,
  TitleInventoryState,
  InventoryStateWrapper,
  MyProcessList,
  BannerContainer,
  BannerTitle,
  BannerMessage,
  BannerActions,
} from './styled'
import { P, SubtitleBold } from 'views/components/UI/Text'
import Select from 'views/components/UI/Select'
import { Button } from 'views/components/UI/Buttons'
import Doughnut from 'views/components/Graphics/Doughnut'
import { platformAdminRoutes } from 'router/routes'
import ModalLoader from 'views/components/UI/ModalLoader/modal-loader'
import { useFetchAllProcesses } from 'hooks/process/useGetAllProcessCurrentCompany/useGetAllProcessCurrentCompany'
import { useGetResumeProcess } from 'hooks/process/useGetResumeProcess/useGetResumeProcess'
import { ItemDraftProcesses, ItemSendedProcess } from './components'
import { cleanProcess } from 'ducks/createProcess'
import { useNotification } from 'lib/context/notification.context'
import { useFinishProcess } from 'hooks/process/useFinishProcess/useFinishProcess'
import { getProcessDetail } from 'services/common/process/createProcess/create-process-service'
import _ from 'lodash'
import { useTranslation } from 'react-i18next'
import { userSelector } from 'ducks/user'
import { Subtitle } from 'views/components/UI/StyledComponents/styled.components'
import fetchSubscriptionSummary, { SubscriptionSummary } from './services/fetch-subscription-summary'

type BannerType = {
  key: string
  title: string
  message: string
}

const UNLIMITED_CREDITS = 1000000000
const INVENTORY_30_PERCENT = 30
const INVENTORY_10_PERCENT = 10
const LICENSE_3_MONTHS = 90
const LICENSE_1_MONTH = 30
const DISMISSED_BANNER_PREFIX = 'bluesite-dismissed-home-banner'

const getBannerPriority = (subscription: SubscriptionSummary | null): BannerType | null => {
  if (!subscription) {
    return null
  }

  const isUnlimited = Number(subscription.credits) >= UNLIMITED_CREDITS
  const availableCredits = Number(subscription.credits_available ?? 0)
  const totalCredits = Number(subscription.credits ?? 0)
  const availablePercent: number =
    isUnlimited || totalCredits <= 0 ? 100 : Math.round((availableCredits / totalCredits) * 100)
  const daysToExpiration: number = dayjs(subscription.expiration_date)
    .startOf('day')
    .diff(dayjs().startOf('day'), 'day')

  if (daysToExpiration <= LICENSE_1_MONTH) {
    return {
      key: 'license-1-month',
      title: 'Tu licencia de Bluesite vence en 1 mes',
      message:
        'La fecha de vencimiento está cada vez más cerca. Si desean seguir utilizando la plataforma Bluesite, este es un buen momento para revisar la renovación. Si ya están trabajando este tema con nosotros, puedes ignorar esta notificación.',
    }
  }

  if (!isUnlimited && availablePercent <= INVENTORY_10_PERCENT) {
    return {
      key: 'inventory-10-percent',
      title: 'Les queda el 10% de su inventario disponible',
      message:
        'El inventario disponible está próximo a agotarse. Si tienen procesos de evaluación previstos, les recomendamos revisar este tema cuanto antes para evitar interrupciones. Si ya están conversando con nuestro equipo, puedes ignorar esta notificación.',
    }
  }

  if (daysToExpiration <= LICENSE_3_MONTHS) {
    return {
      key: 'license-3-months',
      title: 'Tu licencia de Bluesite vence en 3 meses',
      message:
        'Queremos avisarte con tiempo para que puedan planear la continuidad del servicio con tranquilidad. Si ya están revisando este tema con alguien del equipo Bluesite, puedes ignorar esta notificación.',
    }
  }

  if (!isUnlimited && availablePercent <= INVENTORY_30_PERCENT) {
    return {
      key: 'inventory-30-percent',
      title: 'Les queda el 30% de su inventario disponible',
      message:
        'Ya han utilizado la mayor parte del inventario contratado. Si el ritmo de uso se mantiene, este puede ser un buen momento para revisar la renovación del plan Bluesite. Si ya están gestionando este tema con Bluesite, pueden ignorar esta notificación.',
    }
  }

  return null
}

const getDismissedBannerKey = (
  userId: string | number,
  companyId: number,
  subscriptionId: number,
  bannerKey: string
): string => {
  return `${DISMISSED_BANNER_PREFIX}:${userId}:${companyId}:${subscriptionId}:${bannerKey}`
}

const HomePlatformAdministrator = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { companyId, userId } = useSelector(userSelector)
  const role = useSelector((state: any) => state.auth.role)
  const { getError, getWarning } = useNotification()
  const { allProcess, fetchAllProcesses, loadingAllProcesses } = useFetchAllProcesses()

  const { handleFinishProcess } = useFinishProcess()

  const { loadingDetailProcess, processInfo, fetchProcessData, fetchTestsByCompany, testsByCompany } =
    useGetResumeProcess()
  const [process, setProcess] = useState<number | ''>('')
  const [loadingFinishProcess, setLoadingFinishProcess] = useState(false)
  const [useProcess, setUseProcess] = useState<number | number[]>()
  const [allProcessSelect, setAllProcessSelect] = useState([] as any)
  const [banner, setBanner] = useState<BannerType | null>(null)
  const [bannerStorageKey, setBannerStorageKey] = useState<string>('')

  useEffect(() => {
    if (process === '') {
      fetchAllProcesses()
      fetchTestsByCompany(process)
    } else {
      fetchTestsByCompany(process)
    }
  }, [process, fetchAllProcesses, fetchTestsByCompany])

  useEffect(() => {
    const processIDs = allProcess?.map((processFilter) => processFilter.value)
    const allProcessFiltered = allProcess?.filter(
      (item: any, index, self) => index === self.findIndex((ts: any) => ts.process_id === item.process_id)
    )
    setAllProcessSelect(allProcessFiltered)
    const numberProcessIDs = processIDs?.map((id) => Number(id))
    const processToSend = !process ? numberProcessIDs : process

    if (processIDs?.length === 0 || !processToSend) {
      return
    }
    setUseProcess(processToSend)
    fetchProcessData(processToSend)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allProcess, fetchProcessData, process])

  useEffect(() => {
    const loadBanner = async (): Promise<void> => {
      if (role !== 'super_platform_admin' || !companyId || !userId) {
        setBanner(null)
        setBannerStorageKey('')
        return
      }

      const subscription: SubscriptionSummary | null = await fetchSubscriptionSummary(companyId)
      const nextBanner: BannerType | null = getBannerPriority(subscription)

      if (!subscription || !nextBanner) {
        setBanner(null)
        setBannerStorageKey('')
        return
      }

      const dismissedKey: string = getDismissedBannerKey(userId, companyId, subscription.id, nextBanner.key)

      if (localStorage.getItem(dismissedKey) === 'true') {
        setBanner(null)
        setBannerStorageKey('')
        return
      }

      setBanner(nextBanner)
      setBannerStorageKey(dismissedKey)
    }

    loadBanner()
  }, [companyId, role, userId])

  const handleDismissBanner = (): void => {
    if (bannerStorageKey) {
      localStorage.setItem(bannerStorageKey, 'true')
    }

    setBanner(null)
    setBannerStorageKey('')
  }

  const handleCreateProcess = () => {
    dispatch(cleanProcess())
    setTimeout(() => {
      navigate(platformAdminRoutes.CreateProcess)
    }, 1000)
  }

  const onFinishProcess = useCallback(
    (processId: number) => {
      if (!loadingFinishProcess) {
        setLoadingFinishProcess(true)
        getProcessDetail(processId)
          .then((response: any) => {
            setLoadingFinishProcess(false)
            handleFinishProcess(response.process)
          })
          .catch(() => {
            getError('Ha ocurrido un error al cargar este proceso')
            setLoadingFinishProcess(false)
          })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getError]
  )

  const handleSelectChange = (e: any) => {
    if (e) {
      setProcess(parseInt(e))
    } else {
      setProcess('')
    }
  }

  const handleSelectClick = () => {
    if (allProcess.length === 0) {
      getWarning('No tienes procesos asignados')
    }
  }

  let processesToShow: Array<{
    process_id: number
    process_name: string
    active_process: boolean
    testId: number
    test_name: string
  }> = []

  if (processInfo && processInfo.all_processes.length > 0) {
    processesToShow = _.uniqBy(processInfo.all_processes, 'process_id')
    processesToShow = processesToShow.slice(0, 10)
  }

  const renderLabels = () => {
    const labels = [
      `Betesa (${testsByCompany?.categories.Betesa || 0})`,
      `${t('global.common.skillText')} (${testsByCompany?.categories.Skills || 0})`,
      `${t('global.common.threeSixtyText')} (${testsByCompany?.categories.Test360 || 0})`,
    ]
    if (process === '' || process === 0) {
      labels.push(`Pruebas en borrador (${testsByCompany?.draftTests})`)
    }
    return labels
  }

  return (
    <MainLayout>
      {loadingAllProcesses || loadingDetailProcess ? (
        <ModalLoader isLoading={loadingAllProcesses || loadingDetailProcess} />
      ) : (
        <Container>
          <TopContainer>
            <div style={{ display: 'flex', flexDirection: 'row', gap: 16, width: 470, alignItems: 'center' }}>
              <SubtitleBold>Visualizar:</SubtitleBold>
              <Select
                label="Todos los procesos"
                options={allProcessSelect || []}
                value={process}
                onChange={(e) => handleSelectChange(e)}
                onClick={() => handleSelectClick()}
              />
            </div>
            <Button onClick={handleCreateProcess} type="submit">
              Nuevo proceso
            </Button>
          </TopContainer>
          {banner && (
            <BannerContainer>
              <BannerTitle>{banner.title}</BannerTitle>
              <BannerMessage>{banner.message}</BannerMessage>
              <BannerActions>
                <Button
                  type="button"
                  onClick={() => {
                    window.location.href = 'http://api.whatsapp.com/send?phone=3202266690'
                  }}
                >
                  Gestionar la renovación
                </Button>
                <Button variant="ghost" type="button" onClick={handleDismissBanner}>
                  Gracias por la información
                </Button>
              </BannerActions>
            </BannerContainer>
          )}
          <LitleCardsContainer>
            <Link
              to={platformAdminRoutes.Inventory.replace(':companyId', companyId.toString())}
              state={process ? { processSelectedId: process } : {}}
            >
              <InventoryStateWrapper>
                <LittleCard>
                  {process ? (
                    <>
                      <TextLitleCard>Pruebas disponibles</TextLitleCard>
                      <CountTestAvailables>
                        {processInfo?.total_inventories && processInfo?.isUnlimited
                          ? 'Ilimitado'
                          : processInfo?.total_inventories || '0'}
                      </CountTestAvailables>
                    </>
                  ) : null}
                  <TitleInventoryState>Revisa el estado de tus inventarios</TitleInventoryState>
                </LittleCard>
              </InventoryStateWrapper>
            </Link>
          </LitleCardsContainer>
          <BigContainer>
            <BigCard>
              <div style={{ zIndex: 3 }}>
                <Subtitle>Resumen de pruebas</Subtitle>
              </div>
              {processInfo && (
                <ChartContainer>
                  <Doughnut
                    dataset={[
                      testsByCompany?.categories.Betesa || 0,
                      testsByCompany?.categories.Skills || 0,
                      testsByCompany?.categories.Test360 || 0,
                      testsByCompany?.draftTests || 0,
                    ]}
                    type="summary"
                    labels={renderLabels()}
                    totalTests={testsByCompany?.totalTests || 0}
                  />
                </ChartContainer>
              )}
            </BigCard>
            <BigCard>
              <div style={{ zIndex: 3 }}>
                <Subtitle>
                  Pruebas totales contestadas vs. total
                </Subtitle>
              </div>
              {processInfo && (
                <ChartContainer>
                  <Doughnut
                    dataset={[processInfo?.total_tests_generated.total, processInfo?.total_tests_completed]}
                    type="comparison"
                    labels={[
                      `Pruebas generadas (${testsByCompany?.totalTests})`,
                      `Pruebas contestadas (${processInfo?.total_tests_completed})`,
                    ]}
                    totalTests={processInfo?.total_tests_completed}
                    firstColor="#2D749C"
                    secondColor="#C4CDD5"
                  />
                </ChartContainer>
              )}
            </BigCard>

            <MyProcessContainer>
              <Subtitle>
                Mis procesos
              </Subtitle>
              <MyProcessList>
              {processInfo && processInfo.all_processes.length > 0 ? (
                <div
                  style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', width: '100%' }}
                >
                  {_.uniqBy(processesToShow, 'process_id').map((item, indexItem) => {
                    const Component = item.active_process ? ItemSendedProcess : ItemDraftProcesses
                    return (
                      <Component
                        onFinishProcess={onFinishProcess}
                        processId={item.process_id}
                        name={item.process_name}
                        key={`${indexItem + item.process_name}`}
                      />
                    )
                  })}
                </div>
              ) : (
                <P>No existen procesos asociados a esta compañía.</P>
              )}
              </MyProcessList>
              <MyProcessBottomContainer>
                <Link to={platformAdminRoutes.MyProcess} state={useProcess}>
                  <Button type="submit">Ver más</Button>
                </Link>
              </MyProcessBottomContainer>
            </MyProcessContainer>
          </BigContainer>
        </Container>
      )}
    </MainLayout>
  )
}

export default HomePlatformAdministrator
